import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getRoadmap } from '../utils/advice';
import { missingEssentials, essentialLink } from '../utils/profileEssentials';
import './ProgressRoadmap.css';

const CONFETTI = ['🎉', '✨', '🎊', '⭐', '💜', '🟣', '🟢'];

// Chaque étape a :
//  - why  : POURQUOI ce jalon compte (affiché tout le temps, même quand c'est ✓ fait)
//  - hint : la consigne pour l'étape à finaliser (uniquement quand pas encore fait)
//  - actions : action(s) génériques (profil / nettoyer affichent plutôt les vrais éléments manquants)
const STEP_DETAIL = {
  profile:  {
    why: "C'est le carburant de l'IA : tes messages, tes visuels et tes gains en francs sont personnalisés à partir de ces infos. Sans elles, Appee travaille à l'aveugle.",
    hint: "2 min pour compléter les infos manquantes, puis c'est réglé.",
  },
  contacts: {
    why: "Ta base clients est ton actif n°1. Sans contacts, aucune campagne ni relance possible — chaque fiche est une occasion de vente.",
    hint: 'Importe ta base ou fais-la grandir.',
    actions: [{ icon: '📥', label: 'Importer un fichier', link: '/import' }, { icon: '🎁', label: 'Collecter via concours', link: '/acquisition' }],
  },
  sending:  {
    why: "C'est ce qui permet à tes e-mails de partir ET d'arriver en boîte de réception, pas dans les spams.",
    hint: "Choisis ton mode d'envoi : automatique (zéro config) ou ta propre adresse.",
    actions: [{ icon: '✉️', label: "Configurer l'envoi", link: '/settings' }],
  },
  clean:    {
    why: "Une base propre = meilleure délivrabilité, zéro double envoi et beaucoup moins de désabonnements.",
    hint: "Quelques points à corriger avant d'envoyer.",
  },
  campaign: {
    why: "La 1ʳᵉ campagne enclenche la machine : c'est là que ta base commence vraiment à te rapporter.",
    hint: 'Appee la rédige et la cible pour toi.',
    actions: [{ icon: '🚀', label: 'Créer une campagne', link: '/campaigns' }],
  },
  birthday: {
    why: "Le message d'anniversaire est le plus rentable qui existe : automatique, personnel, et tes clients adorent.",
    hint: "Active l'envoi automatique — zéro client oublié.",
    actions: [{ icon: '🎂', label: 'Activer les anniversaires', link: '/birthdays' }],
  },
  grow:     {
    why: "Réveiller un client endormi coûte 5× moins cher que d'en acquérir un nouveau — c'est ton meilleur ROI.",
    hint: 'Va chercher le chiffre d\'affaires qui dort dans ta base.',
    actions: [{ icon: '💰', label: 'Réveiller les dormants', link: '/campaigns' }],
  },
};

export default function ProgressRoadmap() {
  const navigate = useNavigate();
  const { contacts, campaigns, templates, strategyInstances, companyProfile, smtp, tenant } = useApp();

  const { steps, percent, doneCount, total, nextStep, sig } = getRoadmap({
    contacts, campaigns, templates, strategyInstances, companyProfile, smtp, tenant,
  });
  const complete = percent === 100;

  const [hoverIdx, setHoverIdx] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('roadmap_collapsed');
      if (stored === '1') return true;
      if (stored === '0') return false;
      // Aucune préférence encore exprimée : la roadmap sert surtout à l'onboarding.
      // Une fois le parcours bien avancé (≥60 %), on la replie par défaut pour
      // alléger le Dashboard. Le choix manuel (ci-dessus) prime toujours.
      return percent >= 60;
    } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed(c => {
    const n = !c;
    try { localStorage.setItem('roadmap_collapsed', n ? '1' : '0'); } catch { /* noop */ }
    return n;
  });

  useEffect(() => {
    let prev = 0;
    try { prev = parseInt(localStorage.getItem('roadmap_done') || '0', 10) || 0; } catch { /* noop */ }
    if (doneCount > prev) {
      setCelebrate(true);
      const t = setTimeout(() => setCelebrate(false), 3200);
      try { localStorage.setItem('roadmap_done', String(doneCount)); } catch { /* noop */ }
      return () => clearTimeout(t);
    }
    if (doneCount !== prev) { try { localStorage.setItem('roadmap_done', String(doneCount)); } catch { /* noop */ } }
  }, [doneCount]);

  // Contenu de l'infobulle pour une étape donnée.
  // La phrase « pourquoi » (why) s'affiche TOUJOURS — y compris quand l'étape est ✓ faite.
  const tipFor = (step) => {
    if (!step) return null;
    const d = STEP_DETAIL[step.id] || {};

    // Étape déjà faite → on félicite + on rappelle POURQUOI ça compte.
    if (step.done) return { title: `✓ ${step.label}`, sub: step.desc, why: d.why, hint: '', items: [] };

    if (step.id === 'profile') {
      const missing = missingEssentials(companyProfile);
      return {
        title: `À finaliser : ${step.label}`,
        sub: `${missing.length} info${missing.length > 1 ? 's' : ''} à compléter`,
        why: d.why, hint: d.hint,
        items: missing.map(f => ({ icon: f.icon, label: f.label, link: essentialLink(f.key), title: f.why })),
      };
    }
    if (step.id === 'clean') {
      const c = [];
      if (sig?.nNoConsent > 0)   c.push({ icon: '🔒', label: `${sig.nNoConsent} sans consentement`, link: '/contacts',          title: 'Sans accord RGPD — à régulariser avant tout envoi (nLPD).' });
      if (sig?.noEmail > 0)      c.push({ icon: '✉️', label: `${sig.noEmail} sans e-mail`,          link: '/contacts?noEmail=1', title: 'Sans e-mail, impossible de leur écrire.' });
      if (sig?.duplicates > 0)   c.push({ icon: '👥', label: `${sig.duplicates} doublon${sig.duplicates > 1 ? 's' : ''}`, link: '/contacts', title: 'Fusionne-les pour éviter les doubles envois.' });
      if (sig?.nUnreachable > 0) c.push({ icon: '🕳️', label: `${sig.nUnreachable} injoignable${sig.nUnreachable > 1 ? 's' : ''}`, link: '/contacts', title: 'Ni e-mail ni téléphone — complète une coordonnée.' });
      return { title: `À finaliser : ${step.label}`, sub: 'Base clients à nettoyer', why: d.why, hint: d.hint, items: c };
    }
    return {
      title: `À finaliser : ${step.label}`,
      sub: step.desc,
      why: d.why, hint: d.hint || '',
      items: (d.actions || []).map(a => ({ ...a, title: '' })),
    };
  };

  const focusIdx  = nextStep ? steps.findIndex(x => x.id === nextStep.id) : -1;
  const activeIdx = hoverIdx != null ? hoverIdx : focusIdx;
  const activeStep = activeIdx >= 0 ? steps[activeIdx] : null;
  const tip = tipFor(activeStep);
  const arrowLeft = activeIdx >= 0 ? ((activeIdx + 0.5) / total) * 100 : 50;

  return (
    <div className={`rm ${collapsed ? 'is-collapsed' : ''} ${complete ? 'is-complete' : ''}`}>
      {celebrate && (
        <div className="rm-celebrate" aria-hidden="true">
          <div className="rm-celebrate-msg">🎉 Bravo — étape franchie&nbsp;!</div>
          {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} className="rm-confetti" style={{ left: `${(i * 6.2) % 100}%`, animationDelay: `${(i % 8) * 90}ms` }}>{CONFETTI[i % CONFETTI.length]}</span>
          ))}
        </div>
      )}

      {/* En-tête */}
      <div className="rm-head">
        <div className="rm-head-l">
          <div className="rm-title">🗺️ Ton parcours vers le plein potentiel</div>
          <div className="rm-sub">
            {doneCount}/{total} jalons accomplis
            {complete ? <> · <strong>parcours complet 🎉</strong></> : (nextStep && <> · à finaliser : <strong>{nextStep.label}</strong></>)}
          </div>
        </div>
        <div className="rm-head-r">
          <div className="rm-pct-wrap">
            <div className="rm-pct">{percent}%</div>
            <div className="rm-pct-note">{complete ? 'Objectif atteint !' : percent >= 80 ? 'Presque au top !' : percent >= 40 ? 'Belle avancée !' : 'C\'est parti !'}</div>
          </div>
          <button
            type="button"
            className="rm-collapse"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            title={collapsed ? 'Déplier le parcours' : 'Réduire le parcours'}
          >
            <span className="rm-collapse-txt">{collapsed ? 'Déplier' : 'Réduire'}</span>
            <span className={`rm-collapse-chev ${collapsed ? 'down' : ''}`} aria-hidden="true">⌃</span>
          </button>
        </div>
      </div>

      <div className="rm-bar"><div className="rm-bar-fill" style={{ width: `${percent}%` }} /></div>

      {!collapsed && (
        <>
          {/* Timeline — survole un jalon pour voir ses éléments dans l'infobulle */}
          <div className="rm-timeline" onMouseLeave={() => setHoverIdx(null)}>
            <div className="rm-steps">
              {steps.map((st, i) => {
                const isFocus = i === focusIdx;
                const isActive = i === activeIdx;
                const status = st.done ? 'done' : (isFocus ? 'current' : 'todo');
                return (
                  <div
                    key={st.id}
                    role="button"
                    tabIndex={0}
                    className={`rm-step ${status} ${isActive && !complete ? 'active' : ''}`}
                    onClick={() => navigate(st.link)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(st.link); } }}
                    onMouseEnter={() => setHoverIdx(i)}
                    onFocus={() => setHoverIdx(i)}
                  >
                    <span className="rm-dot">{st.done ? '✓' : (isFocus ? st.icon : '')}</span>
                    <span className="rm-card">
                      <span className="rm-num">{i + 1}</span>
                      <span className="rm-ic">{st.icon}</span>
                      <span className="rm-label">{st.label}</span>
                      <span className={`rm-desc ${status}`}>{isFocus ? 'À finaliser' : st.desc}</span>
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Tout est vert → message motivant. Sinon → infobulle de l'étape survolée. */}
            {complete ? (
              <div className="rm-done">
                <div className="rm-done-title">🎉 Parcours complet — chapeau&nbsp;!</div>
                <div className="rm-done-text">
                  Ton compte tourne à plein régime : profil renseigné (des messages à <strong>ta</strong> voix),
                  base clients propre, envois fiables et anniversaires en pilote automatique. C'est exactement
                  cette configuration complète qui paie : <strong>plus de personnalisation, moins de spam, et chaque
                  campagne qui rapporte davantage</strong>. Tu fais partie des rares à exploiter Appee à 100&nbsp;%.
                </div>
              </div>
            ) : (tip && (
              <div className="rm-tip">
                <span className="rm-tip-arrow" style={{ left: `${arrowLeft}%` }} aria-hidden="true" />
                <div className="rm-tip-head">
                  <span className="rm-tip-title">{tip.title}</span>
                  {tip.sub && <span className="rm-tip-sub">{tip.sub}</span>}
                </div>
                {tip.why && <div className="rm-tip-why"><span className="rm-tip-why-lbl">💡 Pourquoi&nbsp;?</span> {tip.why}</div>}
                {tip.hint && <div className="rm-tip-text">{tip.hint}</div>}
                {tip.items.length > 0 && (
                  <div className="rm-tip-chips">
                    {tip.items.map(it => (
                      <button key={it.label} type="button" className="rm-chip" title={it.title} onClick={(e) => { e.stopPropagation(); navigate(it.link); }}>
                        <span className="rm-chip-ic" aria-hidden="true">{it.icon}</span>
                        <span className="rm-chip-label">{it.label}</span>
                        <span className="rm-chip-arrow" aria-hidden="true">→</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {!complete && (
            <button type="button" className="rm-help-link" onClick={() => navigate('/astuces')}>
              ❓ Une question ? Demande à l'assistant →
            </button>
          )}
        </>
      )}
    </div>
  );
}
